import { useCallback, useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import { useBackendText } from '@src/components/assets/editor/wizard/wizard-texts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/components/ui/dialog';
import { Button } from '@src/components/ui/button';
import { AskChoiceBlock } from './AskChoiceBlock';
import { AskSkipButton } from './AskSkipButton';
import { AskItems } from './AskItems';
import { AskValueInput } from './AskValueInput';
import { MarkdownView } from '@src/components/markdown-view';
import { useAskModalStore } from './ask-modal-store';
import { AskAssist } from './AskAssist';
import { fieldsOf, useAskQuestion } from './use-ask-question';

/**
 * A question a ComputeOp put to a person — the MODAL rendering, opened by the
 * `open_ask_modal` ui_command over whatever a live tab is already showing. If
 * that happens to be a wizard's own progress page, it stays visible right
 * behind this dialog — which is exactly why, unlike the full-page `AskView`,
 * this one carries NO wizard-specific link or status of its own: closing (or
 * auto-closing, once settled) is enough to see it, since nothing was ever
 * navigated away from. The no-live-tab fallback (a fresh `win/` window) still
 * gets `AskView` instead — there is no screen behind it there to reveal.
 */
export function AskModalRoot() {
  const open = useAskModalStore((s) => s.open);
  const questionId = useAskModalStore((s) => s.payload);
  const setOpen = useAskModalStore((s) => s.setOpen);
  if (!open || !questionId) return null;
  return <AskModal questionId={questionId} onOpenChange={setOpen} />;
}

function AskModal({ questionId, onOpenChange }: { questionId: string; onOpenChange: (open: boolean) => void }) {
  const { question, hasChoices, values, setValues, error, busy, settledKind, settledMessage, submit, answer, cancel, skip, agentAnswered } =
    useAskQuestion(questionId);
  // The question's words were written by the backend (a compute op's prompt, detail and buttons).
  const bt = useBackendText();
  const items = question?.items ?? [];

  // Settled, however it got there: nothing left for THIS dialog to say — the
  // page it was sitting over (a wizard's own progress view, or anything else)
  // is already the answer to "what happens now", and it is right there the
  // instant this closes.
  useEffect(() => {
    if (settledKind) onOpenChange(false);
  }, [settledKind, onOpenChange]);

  // Esc / a click outside is the person's "no", never just a hidden dialog: an
  // `until_answered` ask has no deadline, so a question dismissed without
  // answering would hold its wizard (and its run slot) until the backend
  // restarts, with nothing left on screen to answer it. Cancelling settles it,
  // and the effect above closes the dialog.
  const dismiss = useCallback(
    (next: boolean) => {
      if (next) return;
      if (settledKind) onOpenChange(false);
      else if (!busy) void cancel();
    },
    [settledKind, busy, cancel, onOpenChange],
  );

  return (
    <Dialog open onOpenChange={dismiss}>
      {/* Above every other dialog (z-50), by z-index rather than by mount order: the
          wizard's own popup can re-mount after a question arrives, and a question
          hidden behind the very popup that raised it cannot be answered. */}
      <DialogContent
        className={items.length > 0 ? 'z-[60] sm:max-w-lg' : 'z-[60] sm:max-w-md'}
        overlayClassName="z-[60]"
        data-testid="ask-modal"
      >
        {settledKind ? (
          settledMessage && (
            <p className="py-2 text-center text-sm text-muted-foreground" data-testid="ask-modal-settled">
              {settledMessage}
            </p>
          )
        ) : !question ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            <Trans>Loading…</Trans>
          </p>
        ) : (
          <>
            {/* pr-6 clears the dialog's close X (pinned top-right): in RTL the title starts at the
                right edge and ran under it. */}
            <DialogHeader className="pr-6">
              <DialogTitle className="whitespace-pre-line" data-testid="ask-modal-prompt">{bt(question.prompt)}</DialogTitle>
              {question.detail ? (
                <DialogDescription data-testid="ask-modal-detail">{bt(question.detail)}</DialogDescription>
              ) : null}
            </DialogHeader>

            {/* The guide is `setup.md` — genuinely helpful beside a field asking
                for a value ("copy the Phone number ID"), and pure noise beside a
                plain yes/no confirm, which has nothing to guide anyone THROUGH
                and whose `setup.md` is written for the next maintainer, not for
                whoever is answering. (Beside "pick a way" cards the backend sends none.) */}
            {question.guide && items.length === 0 && fieldsOf(question.fields).length > 0 ? (
              <div className="rounded border bg-muted/30 p-3 text-sm" data-testid="ask-modal-guide">
                <MarkdownView value={question.guide} compact />
              </div>
            ) : null}

            <AskAssist
              questionId={questionId}
              available={question.assist_available}
              initial={question.assist}
              onAnswered={agentAnswered}
            />

            <AskChoiceBlock
              choices={question.choices}
              busy={busy}
              submitLabel={question.submit_label ? bt(question.submit_label) : undefined}
              onAnswer={(value) => void answer(value)}
            />

            {items.length > 0 ? (
              <AskItems
                items={items}
                busy={busy}
                onAnswer={(value) => void answer(value)}
                onCancel={() => void cancel()}
              />
            ) : null}

            {(hasChoices || items.length > 0 ? [] : fieldsOf(question.fields)).map((name) => (
              <div key={name} className="flex flex-col gap-1">
                {name ? (
                  <label className="text-sm" htmlFor={`ask-modal-${name}`}>
                    {name}
                  </label>
                ) : null}
                <AskValueInput
                  id={`ask-modal-${name}`}
                  testId={`ask-modal-input-${name || 'value'}`}
                  secret={question.secret}
                  file={question.file}
                  value={values[name] ?? ''}
                  onChange={(value) => setValues((prev) => ({ ...prev, [name]: value }))}
                  onEnter={() => void submit()}
                />
              </div>
            ))}

            {error ? (
              <p className="text-sm text-destructive" data-testid="ask-modal-error">
                {error}
              </p>
            ) : null}

            <AskSkipButton skippable={question.skippable} busy={busy} onSkip={() => void skip()} />

            {items.length > 0 ? null : (
              <DialogFooter>
                <Button variant="ghost" disabled={busy} onClick={() => void cancel()} data-testid="ask-modal-cancel">
                  {question.cancel_label ? bt(question.cancel_label) : <Trans>Cancel</Trans>}
                </Button>
                {hasChoices ? null : (
                  <Button disabled={busy} onClick={() => void submit()} data-testid="ask-modal-submit">
                    {question.submit_label ? bt(question.submit_label) : <Trans>Send</Trans>}
                  </Button>
                )}
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
